import React from 'react';
import { AlertCircle, CreditCard, Shield, FileText, UserX, AlertTriangle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

export const Policies = () => {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-[#0B7CB5]">Policies</span>
            </h2>
            <p className="text-xl text-gray-600">
              Clear, transparent policies to ensure the best service for everyone
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem
              value="cancellation"
              className="bg-white border-2 border-gray-100 rounded-xl px-6 hover:border-[#0B7CB5]/30 transition-colors duration-200"
            >
              <AccordionTrigger className="text-left py-6">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="w-6 h-6 text-[#0B7CB5]" />
                  <span className="text-xl font-bold text-gray-900">Cancellation & Rescheduling Policy</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed pb-6 space-y-3">
                <p>We kindly ask for <strong>24 hours&apos; notice</strong> for cancellations or rescheduling.</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>24+ hours notice:</strong> No fee - full flexibility</li>
                  <li><strong>Under 24 hours:</strong> 50% cancellation fee applies</li>
                  <li><strong>No-show or inaccessible property:</strong> Full cleaning fee applies</li>
                  <li><strong>Weather cancellations:</strong> No fee for severe weather conditions</li>
                </ul>
                <p className="text-sm italic mt-3">Last-minute changes impact our team&apos;s schedule and livelihood. We appreciate your understanding.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem
              value="booking"
              className="bg-white border-2 border-gray-100 rounded-xl px-6 hover:border-[#0B7CB5]/30 transition-colors duration-200"
            >
              <AccordionTrigger className="text-left py-6">
                <div className="flex items-center space-x-3">
                  <FileText className="w-6 h-6 text-[#0B7CB5]" />
                  <span className="text-xl font-bold text-gray-900">Booking & Scheduling Policy</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed pb-6 space-y-3">
                <p><strong>Initial Deep Cleaning Required:</strong> All new recurring clients must start with a first-time deep cleaning. This establishes a baseline for consistent service.</p>
                <p><strong>Booking Process:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Quotes are tentative estimates based on normal condition homes</li>
                  <li>Final pricing determined after initial cleaning assessment</li>
                  <li>You must call <a href="tel:+18045837000" className="text-[#0B7CB5] hover:underline">(804) 583-7000</a> or <a href="mailto:custommaidsrva@gmail.com" className="text-[#0B7CB5] hover:underline">email</a> to confirm booking</li>
                  <li>Recurring discounts apply after first-time service</li>
                </ul>
                <p><strong>Access Requirements:</strong> Please provide clear instructions for property access (lockbox code, key location, etc.) at least 24 hours before service.</p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="payment"
              className="bg-white border-2 border-gray-100 rounded-xl px-6 hover:border-[#0B7CB5]/30 transition-colors duration-200"
            >
              <AccordionTrigger className="text-left py-6">
                <div className="flex items-center space-x-3">
                  <CreditCard className="w-6 h-6 text-[#0B7CB5]" />
                  <span className="text-xl font-bold text-gray-900">Payment Policy</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed pb-6 space-y-3">
                <p><strong>We accept:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Cash</li>
                  <li>Card</li>
                  <li>Electronic payments (Zelle, Apple Pay, etc.)</li>
                </ul>
                <p className="mt-4">Payment is due at the time of service unless otherwise arranged.</p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="safety"
              className="bg-white border-2 border-gray-100 rounded-xl px-6 hover:border-[#0B7CB5]/30 transition-colors duration-200"
            >
              <AccordionTrigger className="text-left py-6">
                <div className="flex items-center space-x-3">
                  <Shield className="w-6 h-6 text-[#0B7CB5]" />
                  <span className="text-xl font-bold text-gray-900">Safety & Liability Policy</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed pb-6 space-y-4">
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Property & Belongings</p>
                  <p>Your home and belongings are handled with care. We are fully insured. Any accidental damage will be reported immediately and addressed through our insurance policy.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Pet Safety</p>
                  <p>Pets must be secured if they are nervous, aggressive, or prone to escaping. We love animals but cannot be responsible for pets that escape during service.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Staff Safety</p>
                  <p>To protect our team, we do not:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                    <li>Enter homes with active infestations</li>
                    <li>Clean bodily fluids unless booked as hazardous cleaning</li>
                    <li>Move heavy furniture or appliances</li>
                    <li>Work in unsafe or threatening conditions</li>
                    <li>Clean at heights requiring ladders over 6 feet</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Infestation Policy</p>
                  <p>We do not clean homes with active infestations (roaches, fleas, bedbugs, rodents, etc.) until they are fully exterminated with professional documentation.</p>
                </div>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                  <p className="font-semibold text-gray-900 mb-1">Hazardous Condition Disclosure</p>
                  <p>All hazardous conditions (hoarding, bio-risk, severe neglect, infestations) must be disclosed before booking. Failure to disclose will result in an automatic <strong>$250 hazardous cleaning fee</strong> and possible service refusal.</p>
                </div>
                <p className="text-sm italic">Eco-friendly supplies available upon request at no extra charge.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem
              value="guarantee"
              className="bg-white border-2 border-gray-100 rounded-xl px-6 hover:border-[#0B7CB5]/30 transition-colors duration-200"
            >
              <AccordionTrigger className="text-left py-6">
                <div className="flex items-center space-x-3">
                  <AlertTriangle className="w-6 h-6 text-[#0B7CB5]" />
                  <span className="text-xl font-bold text-gray-900">Service Guarantee & Quality</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed pb-6 space-y-3">
                <p><strong>100% Satisfaction Guarantee:</strong> If you&apos;re not satisfied with any aspect of our service, contact us within 24 hours and we&apos;ll make it right.</p>
                <p><strong>Quality Standards:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>All cleaners are background-checked and trained</li>
                  <li>We use professional-grade equipment and supplies</li>
                  <li>Detailed cleaning checklists for consistency</li>
                </ul>
                <p className="text-sm italic mt-3">Our reputation is built on quality work and satisfied clients.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem
              value="privacy"
              className="bg-white border-2 border-gray-100 rounded-xl px-6 hover:border-[#0B7CB5]/30 transition-colors duration-200"
            >
              <AccordionTrigger className="text-left py-6">
                <div className="flex items-center space-x-3">
                  <UserX className="w-6 h-6 text-[#0B7CB5]" />
                  <span className="text-xl font-bold text-gray-900">Privacy & Confidentiality</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed pb-6 space-y-3">
                <p>We respect your privacy and handle your information with care.</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Confidentiality:</strong> Your personal information and home details remain private</li>
                  <li><strong>No unauthorized photos:</strong> We don&apos;t photograph your home without permission</li>
                  <li><strong>Data security:</strong> Contact and payment info stored securely</li>
                  <li><strong>Professional boundaries:</strong> Our team maintains professional conduct at all times</li>
                </ul>
                <p className="text-sm mt-3">Newsletter subscribers can unsubscribe anytime. We never share or sell your information.</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </section>
  );
};